import TableCell from "./component/TableCell";
import Pagination from "react-bootstrap/Pagination";
import { useCallback } from "react";

const Table = ({
  columnNames,
  items,
  fixSize,
  itemClick,
  isLoading,
  pagination = false,
  currentPage = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange = () => {},
  sortBy,
  sortDirection,
  onSortChange,
}) => {
  const handlePageChange = useCallback(
    (newPage) => {
      onPageChange(newPage);
    },
    [onPageChange]
  );

  const sortIconClass = (sortKey) => {
    if (sortBy !== sortKey) return "fa-solid fa-sort";
    return sortDirection === "asc"
      ? "fa-solid fa-sort-up"
      : "fa-solid fa-sort-down";
  };

  const renderPagination = () => {
    if (!pagination || totalItems <= itemsPerPage) return null;

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-4 text-dark">
        <Pagination className="pagination-primary">
          <Pagination.Prev
            className="mx-1 rounded"
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          />
          {startPage > 1 && (
            <>
              <Pagination.Item
                className="mx-1 rounded"
                active={currentPage === 1}
                onClick={() => handlePageChange(1)}
              >
                1
              </Pagination.Item>
              {startPage > 2 && (
                <Pagination.Ellipsis className="mx-1" disabled />
              )}
            </>
          )}
          {pages}
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <Pagination.Ellipsis className="mx-1" disabled />
              )}
              <Pagination.Item
                className="mx-1 rounded"
                active={currentPage === totalPages}
                onClick={() => handlePageChange(totalPages)}
              >
                {totalPages}
              </Pagination.Item>
            </>
          )}
          <Pagination.Next
            className="mx-1 rounded"
            onClick={() =>
              handlePageChange(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <div>
      <div className="table-responsive">
        <table className={`table table-hover ${fixSize || ""}`}>
          <thead>
            <tr>
              {columnNames.map((column, index) => {
                return (
                  <th
                    key={index}
                    scope="col"
                    className={`${column.colSize ? column.colSize : ""} ${
                      column.sortKey ? "sortable-column" : ""
                    }`}
                  >
                    {column.sortKey ? (
                      <button
                        type="button"
                        className="sort-header"
                        onClick={() => onSortChange(column.sortKey)}
                      >
                        {column.name}
                        <i
                          className={`${sortIconClass(column.sortKey)} ms-2`}
                        />
                      </button>
                    ) : (
                      column.name
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={"100%"}>
                  <span
                    className="spinner-border spinner-border-sm yellow-color"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </td>
              </tr>
            ) : items.length ? (
              items.map((item, index) => (
                <tr
                  key={index}
                  className={`${itemClick && "row-click"}`}
                  onClick={itemClick ? () => itemClick(item) : undefined}
                >
                  {columnNames.map((column, index) => (
                    <TableCell
                      key={index}
                      value={item[column.key]}
                      columnConfig={column}
                      item={item}
                    />
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={"100%"}>No hay registros</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pagination && renderPagination()}
    </div>
  );
};

export default Table;
